/**
 * gtag is Googles tagging library.
 * It is not the same as GTM (Google tag manager) even though it's hosted under googletagmanager.com.
 * It can push to GA4 (google analytics) or Google Ads depending on the tracking id:
 *    - G-...    GA4 measurement ID
 *    - AW-...   Google Ads account/conversion tracking ID
 * Our tag id is set up (in google gui) to forward to both at the same time.
 * GA4 and Ads don’t interpret events identically.
 *    - GA4: flexible event model
 *    - Ads: expects specific conversion parameters
 * Our assumption is that ads ignores what it doesn't understand, so we send everything to both.
 */
export class RGTag {
  static _hasConsent = false;

  static startTracking(googleTrackingId, eventAggregator) {
    // Default to empty array
    window.dataLayer = window.dataLayer || [];

    // First, set consent defaults.
    // From Google: "The default consent should be set very early, before tags read the consent state."
    RGTag._setDefaultConsentDenied();

    // Add Gtag script to DOM
    RGTag._attachGtagScript(googleTrackingId);

    // Subscribe to router
    eventAggregator.subscribe('router:navigation:success', event => {
      let url = window.location.toString();
      RGTag.trackPageView(url, event.instruction.config.title);
    });
  }

  /*
   *  Tracking methods
   */
  // Meta: PageView
  static trackPageView(url, title) {
    RGTag._gtagIfConsent('event', 'page_view', {
      page_location: url,
      page_title: title
    });
  }

  // Meta: Subscribe
  static trackPurchase(transactionId, price) {
    RGTag._gtagIfConsent('event', 'purchase', {
      transaction_id: transactionId,
      value: price,
      currency: 'NOK',
      // tax: 20.0,
      // shipping: 10.0,
      // items: [
      //   {
      //     item_id: 'sku_123',
      //     item_name: 'Product name',
      //     price: 99.0,
      //     quantity: 2
      //   }
      // ]
    });
  }

  // Meta: ViewContent
  static trackViewItem(productArticleNumber, productName) {
    RGTag._gtagIfConsent('event', 'view_item', {
      // currency: 'USD',
      // value: 99.0,
      items: [{
        item_id: productArticleNumber,
        item_name: productName,
        // price: 99.0,
        quantity: 1
      }]
    });
  }

  // Meta: AddToCart
  static trackAddToCart(productArticleNumber, productName, price, quantity = 1) {
    RGTag._gtagIfConsent('event', 'add_to_cart', {
      currency: 'NOK',
      value: price * quantity,
      items: [{
        item_id: productArticleNumber,
        item_name: productName,
        price: price,
        quantity: quantity
      }]
    });
  }

  // Meta: InitiateCheckout
  static trackBeginCheckout(articleNumbers, totalPrice) {
    RGTag._gtagIfConsent('event', 'begin_checkout', {
      currency: 'NOK',
      value: totalPrice,
      items: articleNumbers.map(articleNumber => ({
        item_id: articleNumber,
        // item_name: 'Product name',
        // price: 99.0,
        // quantity: 2
      }))
    });
  }

  // Meta: CompleteRegistration
  static trackSignup() {
    RGTag._gtagIfConsent('event', 'sign_up', {
      method: 'email'
    });
  }

  /*
   *  Consent methods
   */
  static grantConsent() {
    RGTag._hasConsent = true;
    RGTag._gtag('consent', 'update', {
      'ad_personalization': 'granted',
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'analytics_storage': 'granted'
    });
  }

  static _setDefaultConsentDenied() {
    // Deny consent by default
    RGTag._gtag('consent', 'default', {
      'ad_personalization': 'denied', // 	Sets consent for personalized advertising.
      'ad_storage': 'denied',         // 	Enables storage, such as cookies (web) or device identifiers (apps), related to advertising.
      'ad_user_data': 'denied',       // 	Sets consent for sending user data to Google for advertising purposes.
      'analytics_storage': 'denied',  // 	Enables storage, such as cookies (web) or app identifiers (apps), related to analytics, e.g. visit duration.
      //'wait_for_update': 3000,         // any positive integer 	Sets a time in milliseconds to wait for a consent update call.
      personalization_storage: "denied",
      functionality_storage: "denied",
      security_storage: "denied",
    });
  }

  /*
   *  Internal methods
   */
  static _attachGtagScript(googleTrackingId) {
    // Add gtag.js
    // Create script element
    let scriptElement = document.createElement('script');
    scriptElement.async = true;
    scriptElement.src = 'https://www.googletagmanager.com/gtag/js?id=' + googleTrackingId;

    // Attach script to dom before first script
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(scriptElement, firstScript);

    // Init
    RGTag._gtag('js', new Date());
    RGTag._gtag('config', googleTrackingId, {
      send_page_view: false // We track these manually because spa routing isn't picked up automatically anyway.
    });
  }

  static _gtagIfConsent(...args) {
    // Check initialization
    if (!window.dataLayer) {
      console.warn('Trying to track event before gtag function is loaded.');
      return;
    }

    // Check consent
    if (!RGTag._hasConsent) {
      return;
    }

    // Track
    RGTag._gtag(...args);
  }

  /*
   * this:
   *   _gtag('event', 'purchase', { value: 123 });
   * becomes:
   *  dataLayer.push(['event', 'purchase', { value: 123 }]);
   */
  static _gtag() {
    if (!window.dataLayer) {
      console.warn('Trying to track event before gtag function is loaded.');
      return;
    }
    console.info('RGTag tracking:', arguments);
    window.dataLayer.push(arguments);
  }
}
